import { Muxer as Mp4Muxer, ArrayBufferTarget as Mp4Target } from "mp4-muxer";
import { Muxer as WebmMuxer, ArrayBufferTarget as WebmTarget } from "webm-muxer";
import { AudioEngine, SimAudioSink } from "./audio";
import { FIXED_DT } from "./constants";

export const EXPORT_FPS = 60;

/**
 * Sim delta of one video frame: exactly two fixed substeps (60fps video over a
 * 1/120 fixed step). 2 * FIXED_DT is float-exact (doubling), so the particle
 * accumulator drains to exactly zero every frame — the capture never drifts to
 * 1 or 3 substeps the way a real-time delta can.
 */
const FRAME_DELTA = 2 * FIXED_DT;

/**
 * Cycles simulated before capture starts. One cycle settles the field enough
 * for the loop seam: deeper warm-up does not tighten cycle-to-cycle
 * repetition, because the residual variation comes from emit-slot reuse
 * drifting in phase against the clock period (the emit RNG is a function of
 * slot uv, and the circular slot buffer does not realign each cycle), not
 * from an unsettled field.
 */
const WARMUP_CYCLES = 1;

const YIELD_INTERVAL_FRAMES = 8;

const KEYFRAME_INTERVAL_FRAMES = EXPORT_FPS * 2;

const ENCODE_QUEUE_MAX = 16;

const VIDEO_BITS_PER_PIXEL_PER_FRAME = 0.1;

const AUDIO_BITRATE = 192_000;

const AUDIO_CHUNK_SAMPLES = 9600;

export interface LoopExportDeps {
  stepSimFrame(delta: number, audioSink: SimAudioSink): void;
  renderFrame(): void;
  canvas: HTMLCanvasElement;
  audioSampleRate(): number;
  createOfflineAudio(offlineCtx: BaseAudioContext, timeSource: () => number): AudioEngine;
}

export interface LoopExportRequest {
  cycles: number;
  cycleSec: number;
  onProgress(text: string): void;
}

function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve: () => void): void => {
    setTimeout(resolve, 0);
  });
}

interface ExportPipeline {
  videoEncoder: VideoEncoder;
  audioEncoder: AudioEncoder;
  finalize(): { buffer: ArrayBuffer; mimeType: string; extension: string };
  encodeError(): Error | null;
  /** Crop applied to every captured frame; H.264/VP9 require even dimensions. */
  visibleRect: DOMRectInit;
}

/**
 * Probes codec support and builds the encoder + muxer set: H.264 + AAC into
 * mp4 when the platform encoders take them (AAC falls back to Opus-in-mp4),
 * otherwise VP9 + Opus into webm. The H.264 level is picked by resolution
 * (high 4.2 covers 1080p60, high 5.2 covers 4K60). Both encoders share one
 * error slot the capture loop polls each frame.
 */
async function createExportPipeline(canvas: HTMLCanvasElement, sampleRate: number): Promise<ExportPipeline> {
  if (typeof VideoEncoder === "undefined" || typeof AudioEncoder === "undefined") {
    throw new Error("WebCodecs is not supported in this browser");
  }
  const width = canvas.width & ~1;
  const height = canvas.height & ~1;
  if (width < 2 || height < 2) {
    throw new Error(`canvas too small to encode (${canvas.width}x${canvas.height})`);
  }
  const visibleRect: DOMRectInit = { x: 0, y: 0, width, height };
  const bitrate = Math.round(width * height * EXPORT_FPS * VIDEO_BITS_PER_PIXEL_PER_FRAME);
  const avcConfig: VideoEncoderConfig = {
    codec: width * height <= 1920 * 1088 ? "avc1.64002A" : "avc1.640034",
    width,
    height,
    framerate: EXPORT_FPS,
    bitrate,
    avc: { format: "avc" },
  };
  const vp9Config: VideoEncoderConfig = {
    codec: "vp09.00.10.08",
    width,
    height,
    framerate: EXPORT_FPS,
    bitrate,
  };
  const aacConfig: AudioEncoderConfig = {
    codec: "mp4a.40.2",
    sampleRate,
    numberOfChannels: 2,
    bitrate: AUDIO_BITRATE,
  };
  const opusConfig: AudioEncoderConfig = {
    codec: "opus",
    sampleRate,
    numberOfChannels: 2,
    bitrate: AUDIO_BITRATE,
  };

  let error: Error | null = null;
  const onError = (e: DOMException): void => {
    error = new Error(e.message);
  };
  const encodeError = (): Error | null => error;

  const avcOk = (await VideoEncoder.isConfigSupported(avcConfig)).supported === true;
  if (avcOk) {
    const aacOk = (await AudioEncoder.isConfigSupported(aacConfig)).supported === true;
    const audioConfig = aacOk ? aacConfig : opusConfig;
    if (!aacOk && (await AudioEncoder.isConfigSupported(opusConfig)).supported !== true) {
      throw new Error("no supported audio codec (tried AAC, Opus)");
    }
    const muxer = new Mp4Muxer({
      target: new Mp4Target(),
      video: { codec: "avc", width, height, frameRate: EXPORT_FPS },
      audio: { codec: aacOk ? "aac" : "opus", numberOfChannels: 2, sampleRate },
      fastStart: "in-memory",
    });
    const videoEncoder = new VideoEncoder({
      output: (chunk: EncodedVideoChunk, meta?: EncodedVideoChunkMetadata): void => muxer.addVideoChunk(chunk, meta),
      error: onError,
    });
    videoEncoder.configure(avcConfig);
    const audioEncoder = new AudioEncoder({
      output: (chunk: EncodedAudioChunk, meta?: EncodedAudioChunkMetadata): void => muxer.addAudioChunk(chunk, meta),
      error: onError,
    });
    audioEncoder.configure(audioConfig);
    return {
      videoEncoder,
      audioEncoder,
      finalize: (): { buffer: ArrayBuffer; mimeType: string; extension: string } => {
        muxer.finalize();
        return { buffer: muxer.target.buffer, mimeType: "video/mp4", extension: "mp4" };
      },
      encodeError,
      visibleRect,
    };
  }

  if ((await VideoEncoder.isConfigSupported(vp9Config)).supported !== true) {
    throw new Error("no supported video codec (tried H.264, VP9)");
  }
  if ((await AudioEncoder.isConfigSupported(opusConfig)).supported !== true) {
    throw new Error("no supported audio codec (tried Opus)");
  }
  const muxer = new WebmMuxer({
    target: new WebmTarget(),
    video: { codec: "V_VP9", width, height, frameRate: EXPORT_FPS },
    audio: { codec: "A_OPUS", numberOfChannels: 2, sampleRate },
  });
  const videoEncoder = new VideoEncoder({
    output: (chunk: EncodedVideoChunk, meta?: EncodedVideoChunkMetadata): void => muxer.addVideoChunk(chunk, meta),
    error: onError,
  });
  videoEncoder.configure(vp9Config);
  const audioEncoder = new AudioEncoder({
    output: (chunk: EncodedAudioChunk, meta?: EncodedAudioChunkMetadata): void => muxer.addAudioChunk(chunk, meta),
    error: onError,
  });
  audioEncoder.configure(opusConfig);
  return {
    videoEncoder,
    audioEncoder,
    finalize: (): { buffer: ArrayBuffer; mimeType: string; extension: string } => {
      muxer.finalize();
      return { buffer: muxer.target.buffer, mimeType: "video/webm", extension: "webm" };
    },
    encodeError,
    visibleRect,
  };
}

/**
 * Feeds the trimmed offline render into the audio encoder as f32-planar
 * AudioData chunks. Timestamps restart at zero — the warm-up cycle has been
 * cut, so capture audio aligns 1:1 with the video track.
 */
async function encodeRenderedAudio(
  encoder: AudioEncoder,
  rendered: AudioBuffer,
  offsetSamples: number,
  lengthSamples: number,
): Promise<void> {
  const ch0 = rendered.getChannelData(0);
  const ch1 = rendered.getChannelData(1);
  for (let start = 0; start < lengthSamples; start += AUDIO_CHUNK_SAMPLES) {
    const n = Math.min(AUDIO_CHUNK_SAMPLES, lengthSamples - start);
    const data = new Float32Array(n * 2);
    data.set(ch0.subarray(offsetSamples + start, offsetSamples + start + n), 0);
    data.set(ch1.subarray(offsetSamples + start, offsetSamples + start + n), n);
    const audioData = new AudioData({
      format: "f32-planar",
      sampleRate: rendered.sampleRate,
      numberOfFrames: n,
      numberOfChannels: 2,
      timestamp: Math.round((start / rendered.sampleRate) * 1_000_000),
      data,
    });
    encoder.encode(audioData);
    audioData.close();
  }
  await encoder.flush();
}

function downloadBuffer(buffer: ArrayBuffer, filename: string, mimeType: string): void {
  const url = URL.createObjectURL(new Blob([buffer], { type: mimeType }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout((): void => URL.revokeObjectURL(url), 10_000);
}

/**
 * Runs the deterministic capture: one warm-up cycle to let the particle field
 * reach its periodic steady state and lay down the reverb/decay tails that
 * overlap the capture's first frames, then `cycles` performance cycles
 * rendered and encoded frame by frame at EXPORT_FPS. Audio is scheduled into
 * an OfflineAudioContext on the sim clock for the whole run (warm-up
 * included), rendered after the visual pass, and trimmed by one cycle so the
 * loop seam carries the previous cycle's tail. The caller resets the particle
 * field before and after, and gates the realtime frame loop off for the
 * duration. Throws on failure; resolves when the file download has started.
 */
export async function exportLoopVideo(deps: LoopExportDeps, req: LoopExportRequest): Promise<void> {
  const framesPerCycle = Math.round(req.cycleSec * EXPORT_FPS);
  if (framesPerCycle < 1) {
    throw new Error(`cycle too short to capture (${req.cycleSec.toFixed(3)}s)`);
  }
  const warmupFrames = framesPerCycle * WARMUP_CYCLES;
  const captureFrames = framesPerCycle * req.cycles;
  const totalFrames = warmupFrames + captureFrames;
  const sampleRate = deps.audioSampleRate();
  const pipeline = await createExportPipeline(deps.canvas, sampleRate);

  const totalSamples = Math.round((totalFrames / EXPORT_FPS) * sampleRate);
  const offsetSamples = Math.round((warmupFrames / EXPORT_FPS) * sampleRate);
  const offlineCtx = new OfflineAudioContext(2, totalSamples, sampleRate);
  let simTimeSec = 0;
  const offlineAudio = deps.createOfflineAudio(offlineCtx, (): number => simTimeSec);

  const frameUs = 1_000_000 / EXPORT_FPS;
  for (let f = 0; f < totalFrames; f++) {
    simTimeSec = f / EXPORT_FPS;
    deps.stepSimFrame(FRAME_DELTA, offlineAudio);
    if (f >= warmupFrames) {
      const captureIdx = f - warmupFrames;
      deps.renderFrame();
      const frame = new VideoFrame(deps.canvas, {
        timestamp: Math.round(captureIdx * frameUs),
        duration: Math.round(frameUs),
        visibleRect: pipeline.visibleRect,
      });
      pipeline.videoEncoder.encode(frame, { keyFrame: captureIdx % KEYFRAME_INTERVAL_FRAMES === 0 });
      frame.close();
      const err = pipeline.encodeError();
      if (err) {
        throw err;
      }
      while (pipeline.videoEncoder.encodeQueueSize > ENCODE_QUEUE_MAX) {
        await yieldToEventLoop();
      }
    }
    if (f % YIELD_INTERVAL_FRAMES === 0) {
      req.onProgress(
        f < warmupFrames
          ? `warm-up ${f + 1}/${warmupFrames}`
          : `frame ${f - warmupFrames + 1}/${captureFrames}`,
      );
      await yieldToEventLoop();
    }
  }

  req.onProgress("encoding video...");
  await pipeline.videoEncoder.flush();
  pipeline.videoEncoder.close();

  req.onProgress("rendering audio...");
  const rendered = await offlineCtx.startRendering();
  await encodeRenderedAudio(pipeline.audioEncoder, rendered, offsetSamples, totalSamples - offsetSamples);
  pipeline.audioEncoder.close();
  const err = pipeline.encodeError();
  if (err) {
    throw err;
  }

  const out = pipeline.finalize();
  downloadBuffer(out.buffer, `resonella-loop-${req.cycles}x.${out.extension}`, out.mimeType);
  const durationSec = captureFrames / EXPORT_FPS;
  const mb = (out.buffer.byteLength / (1024 * 1024)).toFixed(1);
  req.onProgress(`done — ${durationSec.toFixed(2)}s × loop, ${captureFrames} frames, ${mb} MB (${out.extension})`);
}
